import { Camera } from "@capacitor/camera";
import { Capacitor } from "@capacitor/core";
import styled from "@emotion/styled";
import React from "react";
import ReactPlayer from "react-player";
import { match } from "ts-pattern";
import { canOpenAppSettings, openApplicationSettings } from "../../cordova_plugins";
import { ClockInOutCallType } from "../../messages";
import { STRINGS } from "../../strings";
import { translate } from "../../translate";
import { doNothing } from "../../utils/doNothing";
import { AppButton } from "../Generic/AppButton";
import { AppAbstractModal } from "../Generic/AppModal/AppAbstractModal";
import { AppOverlay } from "../Generic/AppOverlay";
import UploadPhoto from "../UploadPhoto";

export interface PromptRequireSelfieModalProps {
  isOpen: boolean;
  type: "alone" | "with-patient";
  clockType: ClockInOutCallType;
  onSuccess: (imageBase64: string) => void;
  onRequestClose: () => void;
}

const clockTypeText = {
  [ClockInOutCallType.IN]: translate(STRINGS.WE_NEED_SELFIE_TO_VERIFY_BEFORE_CLOCK_IN),
  [ClockInOutCallType.OUT]: translate(STRINGS.WE_NEED_SELFIE_TO_VERIFY_BEFORE_CLOCK_OUT),
} as const;

const PromptRequireSelfieModal = (props: PromptRequireSelfieModalProps) => {
  const [state, setState] = React.useState<"loading" | "success" | "no permission">("loading");

  React.useEffect(() => {
    Camera.checkPermissions()
      .then(({ camera }) => {
        if (camera === "denied") {
          return setState("no permission");
        }
        return setState("success");
      })
      .catch((error) => {
        console.error(error);
        setState("no permission");
      });
  }, []);

  return (
    <AppOverlay>
      <PromptRequireSelfieModal.Root isOpen={props.isOpen} onRequestClose={props.onRequestClose}>
        {match(state)
          .with("loading", () => <PromptRequireSelfieModalLoading />)
          .with("no permission", () => (
            <PromptRequireSelfieModalPermissionDenied onClose={props.onRequestClose} />
          ))
          .with("success", () => <PromptRequireSelfieModalCamera {...props} />)
          .exhaustive()}
      </PromptRequireSelfieModal.Root>
    </AppOverlay>
  );
};

const PromptRequireSelfieModalLoading = () => {
  return <div>LOAINDG....</div>;
};

const PromptRequireSelfieModalPermissionDenied = (props: { onClose: () => void }) => {
  const handleClickOpenSettings = () => {
    if (canOpenAppSettings()) {
      openApplicationSettings();
      props.onClose();
    }
  };
  const videoUrl = (() => {
    switch (Capacitor.getPlatform()) {
      case "ios":
        return "assets/enable-camera-ios.mp4";
      case "android":
        return "assets/enable-camera-android.mp4";
      default:
        return null;
    }
  })();
  return (
    <>
      <PromptRequireSelfieModal.TextWrapper>
        <PromptRequireSelfieModal.Title>
          {translate(STRINGS.PERMISSION_TO_CAMERA_DENIED)}
        </PromptRequireSelfieModal.Title>
        <PromptRequireSelfieModal.Message>
          {translate(STRINGS.GO_TO_SETTINGS_AND_ENALBE_CAMERA_PERMISSION)}
        </PromptRequireSelfieModal.Message>
        {videoUrl !== null && (
          <PromptRequireSelfieModal.Message>
            <ReactPlayer
              url={videoUrl}
              width="100%"
              height="auto"
              objectFit="containt"
              loop
              playing
            />
          </PromptRequireSelfieModal.Message>
        )}
      </PromptRequireSelfieModal.TextWrapper>
      <PromptRequireSelfieModal.Buttons>
        <AppButton onClick={handleClickOpenSettings}>{translate(STRINGS.OPEN_SETTINGS)}</AppButton>
        <AppButton onClick={props.onClose} variant="link" color="black">
          Cancel
        </AppButton>
      </PromptRequireSelfieModal.Buttons>
    </>
  );
};

const PromptRequireSelfieModalCamera = (props: PromptRequireSelfieModalProps) => {
  switch (props.type) {
    case "alone":
      return (
        <SelfieModal
          title={translate(STRINGS.PLEASE_TAKE_A_SELFIE)}
          message={clockTypeText[props.clockType]}
          illustration="assets/take-selfie-background-clock.svg"
          {...props}
        />
      );

    case "with-patient":
      return (
        <SelfieModal
          title={translate(STRINGS.SELFIE_WITH_PATIENT)}
          message={translate(
            props.clockType === ClockInOutCallType.IN
              ? STRINGS.SELFIE_WITH_PATIENT_MESSAGE_CLOCK_IN
              : STRINGS.SELFIE_WITH_PATIENT_MESSAGE_CLOCK_OUT
          )}
          illustration="assets/selfie-with-patient.svg"
          {...props}
        />
      );
  }
};

function SelfieModal(
  props: PromptRequireSelfieModalProps & { title: string; message: string; illustration: string }
) {
  const [photo, setPhoto] = React.useState<string | undefined>();
  const approvePhoto = () => {
    if (photo !== undefined) {
      props.onSuccess(photo);
    }
  };
  const previewImage = photo ?? props.illustration;

  return (
    <>
      <div>
        <PromptRequireSelfieModal.image src={previewImage} />
      </div>
      <PromptRequireSelfieModal.TextWrapper>
        <PromptRequireSelfieModal.Title>{props.title}</PromptRequireSelfieModal.Title>
        <PromptRequireSelfieModal.Message>
          <span>{props.message}</span>
          <span>{translate(STRINGS.DONT_FORGET_TO_SMILE)}</span>
        </PromptRequireSelfieModal.Message>
      </PromptRequireSelfieModal.TextWrapper>
      <UploadPhoto
        photo={photo}
        canUploadFromGallery={false}
        canUploadDocument={false}
        onSelect={setPhoto}
        onClickDisabled={doNothing}
        isSelfie
      >
        {($props) => (
          <PromptRequireSelfieModal.Buttons>
            {match($props)
              .with({ type: "Success" }, () => (
                <>
                  <AppButton onClick={approvePhoto}>{translate(STRINGS.APPROVE)}</AppButton>
                  <AppButton color="black" variant="link" onClick={$props.open}>
                    Retake photo
                  </AppButton>
                </>
              ))
              .otherwise(() => (
                <>
                  <AppButton onClick={$props.open}>{translate(STRINGS.OPEN_CAMERA)}</AppButton>
                  <AppButton color="black" variant="link" onClick={props.onRequestClose}>
                    {translate(STRINGS.NO_THANKS)}
                  </AppButton>
                </>
              ))}
          </PromptRequireSelfieModal.Buttons>
        )}
      </UploadPhoto>
    </>
  );
}

PromptRequireSelfieModal.image = styled.img`
  height: 148px;
`;

PromptRequireSelfieModal.Root = styled(AppAbstractModal)`
  > .container {
    background-color: #fff;
    border-radius: 24px;
    box-sizing: border-box;
    padding: 32px 32px 4px 32px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    text-align: center;
    max-height: 90vh;
  }
`;

PromptRequireSelfieModal.Title = styled.div`
  font-size: 22px;
  font-weight: 800;
  text-align: center;
  align-self: center;
`;

PromptRequireSelfieModal.TextWrapper = styled.div`
  margin: 24px 0;
  padding: 0 12px;
`;

PromptRequireSelfieModal.Message = styled.div`
  align-self: center;
  font-size: 16px;
  font-weight: 500;
  margin-top: 4px;
  text-align: center;
  > span {
    display: block;
    margin: 10px 0;
  }
`;

PromptRequireSelfieModal.Buttons = styled.div`
  display: flex;
  flex-direction: column;
`;

export default PromptRequireSelfieModal;
